"use client";
import React, { useState, useEffect } from "react";
import {
  Search, Music, AlertCircle, Loader2, Guitar,
  Download, X, ChevronLeft, ChevronRight, ZoomIn,
} from "lucide-react";

interface CrawledChord {
  name: string;
  desctitle: string;
  mainImage: string;
  anotherPositionImages: string[];
  notes: string[];
  intervals: string[];
  inversions: string[];
  relates: string[];
}

/* ─────────────────────────────────────────
   IMAGE VIEWER
───────────────────────────────────────── */
const ImageViewer = ({ images, initialIndex, onClose }: any) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageError, setImageError] = useState(false);

  const goToPrevious = () => { setImageError(false); setCurrentIndex((p: number) => p > 0 ? p - 1 : images.length - 1); };
  const goToNext = () => { setImageError(false); setCurrentIndex((p: number) => p < images.length - 1 ? p + 1 : 0); };

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.9)", backdropFilter: "blur(12px)" }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full border transition-colors z-10"
        style={{ backgroundColor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)" }}
      >
        <X className="w-5 h-5 text-white" />
      </button>

      {/* Nav */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 w-11 h-11 flex items-center justify-center rounded-full border transition-colors z-10"
            style={{ backgroundColor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)" }}
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 w-11 h-11 flex items-center justify-center rounded-full border transition-colors z-10"
            style={{ backgroundColor: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.15)" }}
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </>
      )}

      <div className="max-w-3xl w-full flex flex-col items-center gap-4">
        {imageError ? (
          <div className="text-center" style={{ color: "var(--muted-foreground)" }}>
            <AlertCircle className="w-14 h-14 mx-auto mb-3" />
            <p>Không thể tải ảnh</p>
          </div>
        ) : (
          <img
            src={images[currentIndex]}
            alt={`Image ${currentIndex + 1}`}
            onError={() => setImageError(true)}
            className="max-w-full max-h-[80vh] object-contain rounded-2xl"
            style={{ boxShadow: "0 0 60px rgba(200,169,110,0.15)" }}
          />
        )}
        {images.length > 1 && (
          <span
            className="text-sm font-semibold px-4 py-1.5 rounded-full border"
            style={{
              color: "var(--brand-primary)",
              borderColor: "rgba(200,169,110,0.3)",
              backgroundColor: "rgba(200,169,110,0.08)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            {currentIndex + 1} / {images.length}
          </span>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   CHORD IMAGE
───────────────────────────────────────── */
const ChordImage = ({ src, alt, className, onClick, showDownload = false }: any) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleDownload = (e: any) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = src.trim();
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <div
        className={`${className} flex items-center justify-center rounded-xl`}
        style={{ backgroundColor: "var(--background)" }}
      >
        <div className="text-center" style={{ color: "var(--muted-foreground)" }}>
          <AlertCircle className="w-10 h-10 mx-auto mb-2" />
          <p className="text-xs">Ảnh không khả dụng</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className} relative group cursor-pointer`} onClick={onClick}>
      {loading && (
        <div
          className="absolute inset-0 flex items-center justify-center rounded-xl"
          style={{ backgroundColor: "var(--background)" }}
        >
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--brand-primary)" }} />
        </div>
      )}
      <img
        src={src.trim()}
        alt={alt}
        onError={() => { setError(true); setLoading(false); }}
        onLoad={() => setLoading(false)}
        className="w-full h-full object-contain rounded-xl"
      />
      {!loading && !error && (
        <>
          <div className="absolute inset-0 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
          >
            <ZoomIn className="w-7 h-7 text-white" />
          </div>
          {showDownload && (
            <button
              onClick={handleDownload}
              className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center rounded-full border opacity-0 group-hover:opacity-100 transition-all duration-200"
              style={{
                backgroundColor: "rgba(13,13,13,0.8)",
                borderColor: "rgba(200,169,110,0.4)",
              }}
            >
              <Download className="w-3.5 h-3.5" style={{ color: "var(--brand-primary)" }} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   SIMPLE SLIDER
───────────────────────────────────────── */
const SimpleSlider = ({ images, onImageClick, title }: any) => {
  const [startIndex, setStartIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 1024) setItemsPerView(2);
      else setItemsPerView(3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const canGoNext = startIndex + itemsPerView < images.length;
  const canGoPrev = startIndex > 0;
  const visibleImages = images.slice(startIndex, startIndex + itemsPerView);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleImages.map((img: any, idx: any) => (
          <div
            key={startIndex + idx}
            className="relative rounded-2xl overflow-hidden border transition-all duration-200"
            style={{
              backgroundColor: "var(--background)",
              borderColor: "var(--border)",
            }}
            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.borderColor = "rgba(200,169,110,0.45)"}
            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.borderColor = "var(--border)"}
          >
            <div className="aspect-square p-4">
              <ChordImage
                src={img}
                alt={`${title} ${startIndex + idx + 1}`}
                className="w-full h-full"
                onClick={() => onImageClick(startIndex + idx)}
              />
            </div>
            <div
              className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full text-xs font-black"
              style={{
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                fontFamily: "var(--font-montserrat)",
              }}
            >
              {startIndex + idx + 1}
            </div>
          </div>
        ))}
      </div>

      {images.length > itemsPerView && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => canGoPrev && setStartIndex(startIndex - 1)}
            disabled={!canGoPrev}
            className="w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-150 disabled:opacity-30"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.3)",
              color: "var(--brand-primary)",
            }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <span
            className="text-sm font-semibold px-4 py-1.5 rounded-full border"
            style={{
              color: "var(--muted-foreground)",
              borderColor: "var(--border)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            {startIndex + 1}–{Math.min(startIndex + itemsPerView, images.length)}{" "}
            <span style={{ color: "var(--brand-primary)" }}>/ {images.length}</span>
          </span>

          <button
            onClick={() => canGoNext && setStartIndex(startIndex + 1)}
            disabled={!canGoNext}
            className="w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-150 disabled:opacity-30"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.3)",
              color: "var(--brand-primary)",
            }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────
   PARSE
───────────────────────────────────────── */
const parseCrawledText = (json: any) => {
  try {
    if (!json || json.error) return null;
    const notes = json.notesInChord
      ? json.notesInChord.split("-").map((n: string) => n.trim()).filter(Boolean)
      : [];
    const intervals = json.scaleIntervals
      ? json.scaleIntervals.split("-").map((i: string) => i.trim()).filter(Boolean)
      : [];
    return {
      name: json.chordName || "",
      desctitle: json.variousNames || "",
      mainImage: json.mainImage || "",
      anotherPositionImages: [],
      notes,
      intervals,
      inversions: [],
      relates: [],
    };
  } catch {
    return null;
  }
};

/* ─────────────────────────────────────────
   SECTION WRAPPER
───────────────────────────────────────── */
const SectionCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div
    className="rounded-3xl border overflow-hidden"
    style={{
      backgroundColor: "var(--card)",
      borderColor: "var(--border)",
      boxShadow: "0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(200,169,110,0.06)",
    }}
  >
    <div
      className="px-6 py-4 border-b flex items-center gap-3"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="h-1 w-6 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
      <h3
        className="font-black text-base"
        style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
      >
        {title}
      </h3>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const GuitarChordFinder = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [crawledChord, setCrawledChord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [viewerImages, setViewerImages] = useState<any>(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [focused, setFocused] = useState(false);

  const searchChord = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setError("");
    setCrawledChord(null);
    try {
      const capitalized = searchTerm.trim().charAt(0).toUpperCase() + searchTerm.trim().slice(1);
      const encoded = encodeURIComponent(capitalized);
      const response = await fetch(`/api/proxy-chord?chord=${encoded}`);
      const json = await response.json();
      if (json.error) {
        setError(`Không tìm thấy hợp âm "${searchTerm}". Thử lại với tên khác.`);
        return;
      }
      const parsed = parseCrawledText(json);
      if (parsed) setCrawledChord(parsed);
      else setError("Không thể đọc dữ liệu hợp âm.");
    } catch {
      setError("Không thể kết nối đến máy chủ. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  };

  const openImageViewer = (images: any, index: any) => {
    setViewerImages(images);
    setViewerIndex(index);
  };

  const renderChordResult = (data: any) => (
    <div className="space-y-5 animate-fadeIn">

      {/* Chord hero banner */}
      <div
        className="rounded-3xl p-8 text-center border relative overflow-hidden"
        style={{
          backgroundColor: "var(--card)",
          borderColor: "rgba(200,169,110,0.35)",
          boxShadow: "0 0 60px rgba(200,169,110,0.08)",
        }}
      >
        {/* bg glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, rgba(200,169,110,0.08) 0%, transparent 70%)",
          }}
        />
        <div className="relative flex items-center justify-center gap-3 mb-2">
          <Guitar className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            {data.name}
          </h2>
        </div>
        {data.desctitle && (
          <p className="text-sm relative" style={{ color: "var(--muted-foreground)" }}>
            {data.desctitle}
          </p>
        )}
      </div>

      {/* Main grid: image + composition */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Chord diagram */}
        <SectionCard title="Sơ đồ hợp âm chính">
          <div
            className="relative rounded-2xl overflow-hidden border mx-auto max-w-sm"
            style={{ borderColor: "rgba(200,169,110,0.25)", backgroundColor: "var(--background)" }}
          >
            <ChordImage
              src={data.mainImage}
              alt={`Hợp âm ${data.name}`}
              className="w-full h-72"
              onClick={() => openImageViewer([data.mainImage], 0)}
              showDownload
            />
          </div>
          <p
            className="text-xs text-center mt-4"
            style={{ color: "var(--muted-foreground)" }}
          >
            Nhấn vào ảnh để phóng to · Hover để tải xuống
          </p>
        </SectionCard>

        {/* Composition table */}
        <SectionCard title="Cấu tạo hợp âm">
          <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)" }}>
            <table className="min-w-full">
              <thead>
                <tr style={{ backgroundColor: "rgba(200,169,110,0.1)" }}>
                  <th
                    className="px-4 py-3 text-left text-xs font-black uppercase tracking-widest"
                    style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
                  >
                    Thành phần
                  </th>
                  {data.notes.map((_: any, i: number) => (
                    <th
                      key={i}
                      className="px-3 py-3 text-center text-xs font-black"
                      style={{
                        color: "var(--muted-foreground)",
                        fontFamily: "var(--font-montserrat)",
                        borderLeft: "1px solid var(--border)",
                      }}
                    >
                      {i + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderTop: "1px solid var(--border)" }}>
                  <td
                    className="px-4 py-3.5 text-sm font-bold whitespace-nowrap"
                    style={{
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-montserrat)",
                      borderRight: "1px solid var(--border)",
                    }}
                  >
                    🎵 Nốt nhạc
                  </td>
                  {data.notes.map((note: string, i: number) => (
                    <td
                      key={i}
                      className="px-3 py-3.5 text-center font-black text-lg"
                      style={{
                        color: "var(--brand-primary)",
                        fontFamily: "var(--font-montserrat)",
                        borderRight: "1px solid var(--border)",
                        backgroundColor: "rgba(200,169,110,0.04)",
                      }}
                    >
                      {note}
                    </td>
                  ))}
                </tr>
                <tr style={{ borderTop: "1px solid var(--border)" }}>
                  <td
                    className="px-4 py-3.5 text-sm font-bold whitespace-nowrap"
                    style={{
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-montserrat)",
                      borderRight: "1px solid var(--border)",
                    }}
                  >
                    📊 Quãng
                  </td>
                  {data.intervals.map((interval: string, i: number) => (
                    <td
                      key={i}
                      className="px-3 py-3.5 text-center font-semibold text-sm"
                      style={{
                        color: "var(--foreground)",
                        borderRight: "1px solid var(--border)",
                      }}
                    >
                      {interval}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div
            className="mt-5 rounded-xl px-4 py-3.5 border flex items-start gap-3"
            style={{
              backgroundColor: "rgba(200,169,110,0.06)",
              borderColor: "rgba(200,169,110,0.2)",
            }}
          >
            <span className="text-base shrink-0">💡</span>
            <p className="text-xs leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              <strong style={{ color: "var(--foreground)" }}>Mẹo:</strong> Luyện tập từng nốt riêng lẻ trước khi bấm cả hợp âm — đảm bảo mỗi nốt đều rõ ràng.
            </p>
          </div>
        </SectionCard>
      </div>

      {/* Alternative positions */}
      {data.anotherPositionImages.length > 0 && (
        <SectionCard title={`Các thế bấm khác – ${data.name}`}>
          <SimpleSlider
            images={data.anotherPositionImages}
            onImageClick={(idx: number) => openImageViewer(data.anotherPositionImages, idx)}
            title={`Thế bấm ${data.name}`}
          />
          <div
            className="mt-5 rounded-xl px-4 py-3.5 border flex items-start gap-3"
            style={{ backgroundColor: "rgba(200,169,110,0.06)", borderColor: "rgba(200,169,110,0.2)" }}
          >
            <span className="text-base shrink-0">🔄</span>
            <p className="text-xs leading-relaxed" style={{ color: "var(--muted-foreground)" }}>
              <strong style={{ color: "var(--foreground)" }}>Gợi ý:</strong> Thử các thế bấm khác nhau để tìm vị trí thoải mái nhất cho đôi tay của bạn.
            </p>
          </div>
        </SectionCard>
      )}

      {/* Inversions */}
      {data.inversions.length > 0 && (
        <SectionCard title={`Thể đảo – ${data.name}`}>
          <SimpleSlider
            images={data.inversions}
            onImageClick={(idx: number) => openImageViewer(data.inversions, idx)}
            title={`Thể đảo ${data.name}`}
          />
        </SectionCard>
      )}

      {/* Related */}
      {data.relates.length > 0 && (
        <SectionCard title={`Hợp âm liên quan – ${data.name}`}>
          <SimpleSlider
            images={data.relates}
            onImageClick={(idx: number) => openImageViewer(data.relates, idx)}
            title={`Liên quan ${data.name}`}
          />
        </SectionCard>
      )}
    </div>
  );

  return (
    <div
      className="min-h-screen py-10 sm:py-16 px-4"
      style={{ backgroundColor: "var(--background)", fontFamily: "var(--font-roboto)" }}
    >
      <div className="max-w-5xl mx-auto">

        {/* ── HEADER ── */}
        <header className="text-center mb-12">
     

          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-5 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <Guitar className="w-4 h-4" />
            Công cụ tra cứu hợp âm
          </div>

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Guitar{" "}
            <span
              className="relative inline-block"
              style={{ color: "var(--brand-primary)" }}
            >
              Hợp Âm
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>
          </h1>

          <p className="text-base sm:text-lg max-w-lg mx-auto" style={{ color: "var(--muted-foreground)" }}>
            Tra cứu sơ đồ và cấu tạo bất kỳ hợp âm guitar nào — miễn phí, không cần cài app.
          </p>

          <div className="mt-7 flex items-center justify-center gap-3">
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
          </div>
        </header>

        {/* ── SEARCH ── */}
        <div
          className="rounded-3xl border p-6 sm:p-8 mb-8"
          style={{
            backgroundColor: "var(--card)",
            borderColor: "rgba(200,169,110,0.25)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
          }}
        >
          <label
            className="block text-xs font-bold uppercase tracking-widest text-center mb-5"
            style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
          >
            Nhập tên hợp âm bạn muốn tìm
          </label>

          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto">
            {/* Input */}
            <div className="relative flex-1">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: focused ? "var(--brand-primary)" : "var(--muted-foreground)" }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchChord()}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Ví dụ: C, Am, F7, Gsus4..."
                className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm outline-none transition-all duration-200"
                style={{
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                  border: focused
                    ? "1.5px solid rgba(200,169,110,0.7)"
                    : "1.5px solid var(--border)",
                  boxShadow: focused ? "0 0 0 3px rgba(200,169,110,0.1)" : "none",
                  fontFamily: "var(--font-roboto)",
                }}
              />
            </div>

            {/* Button */}
            <button
              onClick={searchChord}
              disabled={loading || !searchTerm.trim()}
              className="flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              style={{
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                fontFamily: "var(--font-montserrat)",
                boxShadow: !loading && searchTerm.trim() ? "0 4px 16px rgba(200,169,110,0.3)" : "none",
              }}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Đang tìm...</>
              ) : (
                <><Search className="w-4 h-4" />Tìm kiếm</>
              )}
            </button>
          </div>

          {/* Suggestions */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>Thử ngay:</span>
            {["C", "Am", "F7", "Gmaj7", "Dsus4", "Bm"].map((chord) => (
              <button
                key={chord}
                onClick={() => { setSearchTerm(chord); }}
                className="px-3 py-1 rounded-full text-xs font-bold border transition-all duration-150"
                style={{
                  fontFamily: "var(--font-montserrat)",
                  backgroundColor: "rgba(200,169,110,0.08)",
                  borderColor: "rgba(200,169,110,0.25)",
                  color: "var(--brand-primary)",
                }}
                onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(200,169,110,0.15)"}
                onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(200,169,110,0.08)"}
              >
                {chord}
              </button>
            ))}
          </div>
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div
            className="mb-6 p-4 sm:p-5 rounded-2xl border flex items-start gap-3 animate-fadeIn"
            style={{
              backgroundColor: "rgba(239,68,68,0.08)",
              borderColor: "rgba(239,68,68,0.3)",
            }}
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
            <div>
              <p className="font-bold text-sm mb-0.5" style={{ color: "#ef4444", fontFamily: "var(--font-montserrat)" }}>
                Không tìm thấy hợp âm
              </p>
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>{error}</p>
            </div>
          </div>
        )}

        {/* ── LOADING ── */}
        {loading && (
          <div
            className="text-center py-16 rounded-3xl border"
            style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}
          >
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: "var(--brand-primary)" }} />
            <h3
              className="text-lg font-black mb-1"
              style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
            >
              Đang tìm kiếm...
            </h3>
            <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>Vui lòng đợi trong giây lát</p>
          </div>
        )}

        {/* ── RESULTS ── */}
        {crawledChord && renderChordResult(crawledChord)}

        {/* ── FOOTER ── */}
        <footer
          className="mt-12 text-center py-8 px-6 rounded-3xl border"
          style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}
        >
          <div className="h-1 w-10 rounded-full mx-auto mb-5" style={{ backgroundColor: "var(--brand-primary)" }} />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Guitar className="w-5 h-5" style={{ color: "var(--brand-primary)" }} />
            <h4
              className="font-black text-base"
              style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
            >
              Chúc bạn học guitar vui vẻ!
            </h4>
          </div>
          <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
            Luyện tập đều đặn là chìa khóa để tiến bộ 🎼
          </p>
        </footer>
      </div>

      {/* ── IMAGE VIEWER MODAL ── */}
      {viewerImages && (
        <ImageViewer
          images={viewerImages}
          initialIndex={viewerIndex}
          onClose={() => setViewerImages(null)}
        />
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default GuitarChordFinder;